<template>
  <div id="graphics-container" class="flex pt-14 justify-evenly"></div>
</template>

<script>
import p5 from "p5";
import { store } from "../store.js";
import { renderBlob, renderBlobScaled } from "../js/render.js";
import SoftBody from "../js/SoftBody.js";
import Cell from "../js/Cell.js";
import { vec2, download, distance } from "../js/core.js";
import { pointInPolygon } from "../js/pointInPolygon";
import { colors, getComplement, setTint } from "../js/palettes";
import { Engine, Runner, World } from "matter-js";
import { createPetriDish } from "../js/petriDish";
import { Composite } from "matter-js";

let engine = Engine.create();
engine.gravity.y = 0;
let runner = Runner.create();
runner.delta = 1000 / 120;
store.runnerInstance = runner;
Runner.run(runner, engine);

const sketch = (p) => {
  store.p5 = p;
  let blobs = [];
  let bb = store.backgroundBrightness;
  let exportRes;
  let outRes = store.outRes;
  let [a1, a2, a3] = [254, 254, 254];
  let vid;
  let ctx;
  let HME;
  let recorder;
  let dish;
  let maxDuration;
  p.toggleSimulation = () => {
    runner.enabled = !runner.enabled;
  };

  p.setup = () => {
    let el = document.querySelector("#graphics-container");
    let size = el.clientHeight * 0.82;
    p.pixelDensity(1);
    p.createCanvas(size, size, p.WEBGL).parent("graphics-container");
    p.background(255,1)
    dish = createPetriDish(p.width * 0.5, p.height * 0.5, p.height * 0.5, 200);
    Composite.add(engine.world, dish);

    store.canvasCenter = vec2(p.width * 0.5, p.height * 0.5);
    store.ratio = outRes / p.width;
    let row = Math.floor(Math.random() * colors.length);
    let col = Math.floor(Math.random() * colors[row].length);
    let complements = getComplement(row, col);
    let complement = Math.floor(Math.random() * complements.length);
    blobs.push(
      new SoftBody(
        new Cell({
          center: vec2(p.height * 0.5, p.width * 0.5),
          radius: p.random(20, 30),
          primaryColor: p.color(...colors[row][col], a1),
          secondaryColor: p.color(...complements[complement], a2),
          edgeColor: p.color(...setTint(complements[complement], 0.97), a3),
        })
      )
    );
    blobs[0].addToMatter(engine.world);
    HME = window.HME
  }; //end of setup

  p.startRecording = () => {
    exportRes = store.exportOptions[0].size;
    maxDuration = store.exportOptions[1].duration;

    console.log(`Start recording: ${exportRes}x${exportRes} ${maxDuration}s`);
    vid = p.createGraphics(exportRes, exportRes, p.WEBGL);
    vid.background(bb, bb, bb, 254);
    vid.noStroke();
    ctx = vid.elt.getContext("webgl");

    HME.createH264MP4Encoder().then((encoder) => {
      recorder = encoder;
      recorder.width = exportRes;
      recorder.height = exportRes;
      recorder.frameRate = 60;
      recorder.quantizationParameter = 17;
      recorder.outputFilename = "hik-video.mp4";
      recorder.initialize();
      if (!store.recording && exportRes < 3000 && runner.enabled) {
        store.recording = true;
      }
    });
  };
  p.stopRecording = () => {
    store.recording = !store.recording;
    recorder.finalize();
    const out = recorder.FS.readFile(recorder.outputFilename);
    download(URL.createObjectURL(new Blob([out], { type: "video/mp4" })));
    store.totalFramesRecorded = 0;
    recorder.delete();
    console.log("stopped");
  };
  p.draw = () => {
    if (p.frameCount % 60 == 0) {
      store.frameRate = Math.floor(p.frameRate());
    }
    p.translate(-p.width * 0.5, -p.height * 0.5);
    p.background(bb, bb, bb, 254);

    p.blendMode(p.DARKEST)
    blobs.forEach((blob) => {
      p.noStroke();
      renderBlob(blob, p);

      if (runner.enabled) {
        blob.move();
        blob.stretch();
      }
      if (blob.cell.id == store.selectedBlob) {
        let border = blob.getBorderNodes();
        p.stroke(0);
        for (let i = 0; i < border.length; i++) {
          let nexti = (i + 1) % border.length;
          p.line(
            border[i].position.x,
            border[i].position.y,
            border[nexti].position.x,
            border[nexti].position.y
          );
        }
      }
      if (store.recording) {
        vid.push();
        vid.blendMode(p.DARKEST)
        vid.translate(-exportRes * 0.5, -exportRes * 0.5);
        renderBlobScaled(blob, vid, exportRes / p.width);
        vid.pop();
      }
    });
    if (store.recording) {
      let frame = new Uint8Array(exportRes * exportRes * 4);
      ctx.readPixels(
        0,
        0,
        exportRes,
        exportRes,
        ctx.RGBA,
        ctx.UNSIGNED_BYTE,
        frame
      );
      recorder.addFrameRgba(frame);
      store.totalFramesRecorded++;
      vid.background(bb, bb, bb, 254);
      if (store.totalFramesRecorded == maxDuration * 60) {
        p.stopRecording();
      }
    }
    //DELETE
    // dish.forEach(r=>{
    //   p.push()
    //   p.translate(r.position.x,r.position.y)
    //   p.rotate(r.angle)
    //   p.rect(0,0,r.width,10)
    //   p.pop()
    // })
  }; //end of draw

  p.addBlob = async function () {
    if (store.color1 == null || store.color2 == null) {
      return;
    }
    let found = false;
    let numTries = 0;
    let newBlob;
    while (!found && numTries < 250) {
      let b = new SoftBody(
        new Cell({
          center: vec2(
            p.random(p.width * 0.25, p.width * 0.75),
            p.random(p.height * 0.25, p.height * 0.75)
          ),
          radius: store.params[0],
          complexity: store.params[1],
          palette: store.currentPalette,
          primaryColor: p.color(...store.color1, a1),
          secondaryColor: p.color(...store.color2, a2),
          edgeColor: p.color(...store.color3, a3),
        })
      );
      let allOutside = true;
      let withinDish = true;
      blobs.forEach((blob) => {
        b.bodies.forEach((body) => {
          let within = pointInPolygon(
            [body.position.x, body.position.y],
            blob.getEdges()
          );

          if (within) {
            allOutside = false;
          }
        });
      });
      b.bodies.forEach((b) => {
        let distToCenter = distance(
          b.position,
          vec2(p.width * 0.5, p.height * 0.5)
        );
        if (distToCenter > p.width * 0.48) {
          withinDish = false;
        }
      });
      if (allOutside && withinDish) {
        found = true;
        console.log(`A cell could be found. Number of tries: ${numTries}`);
        newBlob = b;
        newBlob.addToMatter(engine.world);
        blobs.push(newBlob);
        store.findingCell = false;
      }
      numTries++;
    }

    if (!newBlob) {
      store.findingCell = false;
      alert(
        "There's not enough space for a new cell. Try reducing the size or complexity, or delete some cells."
      );
    }
    return newBlob;
  };

  p.deleteBlobs = () => {
    blobs.forEach((blob) => {
      blob.bodies.forEach((body) => {
        World.remove(engine.world, body);
      });
    });
    blobs = [];
  };
  p.mousePressed = () => {
    if (p.mouseX > 0 && p.mouseY > 0) {
      //mouse within canvas
      store.selectedBlob = null;
      blobs.forEach((blob) => {
        if (pointInPolygon([p.mouseX, p.mouseY], blob.getEdges())) {
          store.selectedBlob = blob.cell.id;
        }
      });
    }
  };
  p.deleteSelectedBlob = () => {
    blobs.forEach((blob) => {
      if (blob.cell.id == store.selectedBlob) {
        blob.bodies.forEach((body) => {
          World.remove(engine.world, body);
        });
      }
    });
    blobs = blobs.filter((b) => b.cell.id != store.selectedBlob);
    store.selectedBlob = null;
  };

  p.exportImage = () => {
    let res = store.exportOptions[0].size;
    let outputImage = p.createGraphics(res, res, p.WEBGL);
    outputImage.noStroke();
    outputImage.background(bb, bb, bb, 254);
    blobs.forEach((blob) => {
      outputImage.push();
      outputImage.translate(-res * 0.5, -res * 0.5);
      outputImage.blendMode(p.DARKEST)
      renderBlobScaled(blob, outputImage, res / p.width);
      outputImage.pop();
    });
    p.save(outputImage, `HiKs_image.png`);
  };
};

export default {
  name: "MainGraphics",
  mounted: async function () {
    await this.loadHME();
    store.P5Instance = new p5(sketch);
  },
  methods: {
    loadHME: async function () {
      const script = document.createElement("script");
      script.setAttribute(
        "src",
        "https://unpkg.com/h264-mp4-encoder/embuild/dist/h264-mp4-encoder.web.js"
      );
      document.body.appendChild(script);
    },
  },
  data: () => ({
    store,
  }),
};
</script>
<style>
#graphics-container {
  width: 70vw;
  display: flex;
  justify-content: space-evenly;
}
</style>